/**
 * Shared utility functions for tranche assignment and management
 * Used by both gift transfer modal and basis table for consistency
 */

/**
 * Converts number to letters: 0->A, 1->B, 25->Z, 26->AA, etc.
 */
function numberToLetters(num) {
	let letters = '';
	while (num >= 0) {
		letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[num % 26] + letters;
		num = Math.floor(num / 26) - 1;
	}
	return letters;
}

/**
 * Converts letters to numbers: A->1, B->2, Z->26, AA->27, etc.
 */
function lettersToNumbers(ltrs) {
	if (!ltrs) return '?';
	let n = 0;
	for (let p = 0; p < ltrs.length; p++) {
		n = ltrs[p].charCodeAt() - 64 + n * 26;
	}
	return n.toString().padStart(6, '0');;
}

/**
 * Sorts transactions according to gift transfer logic based on client type
 */
function sortByClassThenBasisThenDateThenQty(transArray, client) {
	const isVERM = ['vermeer'].includes(client);
	const isBB = ['bushbros', 'onwardfe'].includes(client);

	return transArray.sort((a, b) => {
		if (isVERM) {
			// VERM: shareClass -> date (oldest first) -> sharePrice (highest first) -> quantity
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return -1;
			if (dateA > dateB) return 1;
		} else if (!isBB) {
			// Non-BB: date (newest first) -> shareClass -> sharePrice (highest first) -> quantity
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return 1;
			if (dateA > dateB) return -1;
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;
		}

		// All clients: sharePrice (highest first)
		let sharePriceA = isNaN(a.sharePrice) ? 0 : Number(a.sharePrice);
		let sharePriceB = isNaN(b.sharePrice) ? 0 : Number(b.sharePrice);
		if (sharePriceA < sharePriceB) return 1;
		if (sharePriceA > sharePriceB) return -1;

		if (isBB) {
			// BB: sharePrice -> shareClass -> date (oldest first) -> quantity
			if (a.shareClass < b.shareClass) return -1;
			if (a.shareClass > b.shareClass) return 1;
			let dateA = Number(a.date);
			let dateB = Number(b.date);
			if (dateA < dateB) return -1;
			if (dateA > dateB) return 1;
		}

		// All clients: quantity (smallest first)
		let quantityA = Number(a.quantity);
		let quantityB = Number(b.quantity);
		if (quantityA < quantityB) return -1;
		if (quantityA > quantityB) return 1;
		return 0;
	});
}

/**
 * Assigns tranche letters to transactions using the same logic as gift transfer modal
 * @param {Array} giftTransactions - Transactions for gifting
 * @param {Array} historicHoldings - Historical transaction data
 * @param {Array} priorTransfers - Prior transfer data
 * @param {string} client - Client identifier for sorting logic
 * @returns {Object} Updated arrays with tranche letters assigned
 */
function assignTrancheLetters(giftTransactions, historicHoldings, priorTransfers = [], client) {
	// 1. Sort all arrays using gift transfer logic
	sortByClassThenBasisThenDateThenQty(giftTransactions, client);
	sortByClassThenBasisThenDateThenQty(historicHoldings, client);
	if (priorTransfers.length) {
		sortByClassThenBasisThenDateThenQty(priorTransfers, client);
	}

	// 2. Apply secondary sorting for existing tranche assignments
	historicHoldings.sort((a, b) => {
		if (!a.tranche || !b.tranche) return 0;
		let [classA, dateA] = a.tranche.toString().split('-');
		let [classB, dateB] = b.tranche.toString().split('-');
		dateA = Number(dateA);
		dateB = Number(dateB);
		if (dateA < dateB) return 1;
		if (dateA > dateB) return -1;
		if (classA < classB) return -1;
		if (classA > classB) return 1;
		return 0;
	});

	// 3. Assign tranche letters sequentially
	let letterCount = 0;

	// Process giftTransactions first
	giftTransactions.forEach(function (h, i) {
		let trancheLetter = numberToLetters(letterCount);
		if (!h.tranche || h.tranche.length > 3) {
			h.tranche = trancheLetter;
			letterCount++;
		}
		if (!h.before) h.before = h.quantity;
	});

	// Then process historicHoldings
	historicHoldings.forEach(function (h, i) {
		let trancheLetter = numberToLetters(letterCount);
		if (!h.tranche) {
			h.tranche = trancheLetter;
			letterCount++;
		} else if (h.tranche.length > 3) {
			let timeStamp = h.tranche;
			// Update all transactions with the same timestamp
			historicHoldings.forEach((k) => {
				if (k.tranche == timeStamp) {
					k.tranche = trancheLetter;
				}
			});
			priorTransfers.forEach((p) => {
				if (p.tranche == timeStamp) p.tranche = trancheLetter;
			});
			letterCount++;
		}
		if (!h.before) h.before = h.quantity;
	});

	return { giftTransactions, historicHoldings, priorTransfers };
}

/**
 * Simple tranche assignment for basis table transactions - groups by transaction characteristics
 * @param {Array} transactions - Array of transactions to assign tranches to
 * @param {string} client - Client identifier for sorting logic
 * @returns {Array} Transactions with tranche letters assigned
 */
function assignSimpleTrancheLetters(transactions, client) {
	// Clone transactions to avoid modifying the original array
	const workingTransactions = transactions.map((t) => ({ ...t }));

	// Sort transactions using the same logic as gift transfer
	sortByClassThenBasisThenDateThenQty(workingTransactions, client);

	// Group transactions by key characteristics and assign tranche letters
	let letterCount = 0;
	let assignedGroups = new Map();

	workingTransactions.forEach((transaction) => {
		// Create a grouping key based on transaction characteristics
		// Use certificate number as primary grouping if available, otherwise use notes pattern
		let groupKey;
		let certMatch = transaction.notes?.match(/#([A-Z0-9-]+)/);

		if (certMatch) {
			// Group by certificate number
			groupKey = `cert:${certMatch[1]}`;
		} else {
			// Group by transaction type/pattern in notes
			let notes = transaction.notes || 'Share Grant';

			// Extract common patterns
			if (notes.includes('Certificate Issuance')) {
				groupKey = 'cert-issuance';
			} else if (notes.includes('Distribution from')) {
				groupKey = 'distribution';
			} else if (notes.includes('Stock Grant')) {
				groupKey = 'stock-grant';
			} else if (notes.includes('Share Grant')) {
				// Extract grant number if present
				let grantMatch = notes.match(/Share Grant (\d+)/);
				groupKey = grantMatch ? `share-grant-${grantMatch[1]}` : 'share-grant-unknown';
			} else {
				// Fallback: group by exact notes
				groupKey = `other:${notes}`;
			}
		}

		// Assign tranche letter based on grouping
		if (!assignedGroups.has(groupKey)) {
			assignedGroups.set(groupKey, numberToLetters(letterCount));
			letterCount++;
		}

		transaction.tranche = assignedGroups.get(groupKey);
	});

	return workingTransactions;
}

// Export functions for use in other modules
window.TrancheUtils = {
	numberToLetters,
	lettersToNumbers,
	sortByClassThenBasisThenDateThenQty,
	assignTrancheLetters,
	assignSimpleTrancheLetters,
};

export { numberToLetters, lettersToNumbers, sortByClassThenBasisThenDateThenQty, assignTrancheLetters, assignSimpleTrancheLetters };
